import { Avatar, Box, Center, Flex, Icon, Tag, TagLabel, TagLeftIcon, Text, Tooltip } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { CalendarIcon, CheckCircleFilledIcon, ClockFillIcon } from '../../assets/icons'
import formatDate from '../../helpers/formatDate'
import getIconPath from '../../helpers/getFileIcon'
import getFileNameFromS3 from '../../helpers/getFileNameFromS3'
import { statusColors } from '../../themes/components/tag'
import { DocumentType } from '../../types/document/documentTypes'
import LazyLoadImg from '../image/LazyLoadImg'
import { useTranslation } from 'react-i18next'

const MotionBox = motion(Box)

const DocumentCard = ({
  type,
  title,
  signatureDone,
  document,
  timestamp,
  author,
  onTagClick,
  canSign,
  onSelect,
  isSelected,
}: {
  title: string
  type: DocumentType
  signatureDone?: boolean | null
  timestamp: string
  document: string
  author: {
    name: string
    avatar: string
  }
  onTagClick?: () => void
  canSign?: boolean
  onSelect?: () => void
  isSelected?: boolean
}) => {
  const { t } = useTranslation()
  const statusColor = signatureDone ? 'success' : 'warning'

  const file = getFileNameFromS3(document)

  const isSelectable = onSelect && isSelected !== undefined

  const isImage =
    file?.fileType === 'png' ||
    file?.fileType === 'jpg' ||
    file?.fileType === 'jpeg' ||
    file?.fileType === 'gif' ||
    file?.fileType === 'webp'

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      w="100%"
      h="100%"
      borderRadius="16px"
      bgColor="white"
      pt="12px"
      pos="relative"
      overflow="hidden"
      cursor="pointer"
      role="group"
      boxShadow="0 2px 8px 0 rgba(0,0,0,0.1), 0 1px 3px -1px rgba(0,0,0,0.08)"
      _hover={{
        boxShadow: '0 6px 16px 0 rgba(0,0,0,0.14), 0 3px 8px -1px rgba(0,0,0,0.1)',
        transform: 'translateY(-2px)',
      }}
      style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
      onClick={() => {
        if (isSelectable) {
          onSelect()
        } else {
          window.open(document, '_blank')
        }
      }}
    >
      {/* Selection indicator */}
      {isSelected && (
        <Icon
          w="24px"
          h="24px"
          viewBox="0 0 24 24"
          color="primary.100"
          top="16px"
          right="16px"
          pos="absolute"
          zIndex="2"
        >
          <CheckCircleFilledIcon />
        </Icon>
      )}

      {/* Preview Container */}
      <Box
        w="90%"
        h="200px"
        mx="auto"
        bg="solid.50"
        display="flex"
        justifyContent="center"
        alignItems="center"
        borderTopRadius="8px"
        boxShadow="0 2px 8px 0 rgba(0,0,0,0.08), 0 1px 3px -1px rgba(0,0,0,0.04)"
        pos="relative"
        overflow="hidden"
      >
        {isImage ? (
          <Box
            pos="absolute"
            top="0"
            left="0"
            w="100%"
            h="100%"
            sx={{ '& img': { objectFit: 'cover', objectPosition: 'top' } }}
          >
            <LazyLoadImg src={document} alt={title} w="100%" h="100%" />
          </Box>
        ) : (
          <Center pos="absolute" top="0" left="0" w="100%" h="100%" flexDirection="column" gap="8px" bg="white">
            <Box
              w="56px"
              h="56px"
              bgImage={getIconPath(document)}
              bgSize="contain"
              bgRepeat="no-repeat"
              bgPosition="center"
            />
            {file?.fileType && (
              <Text fontSize="11px" color="solid.400" fontWeight="500" textTransform="uppercase">
                {file.fileType}
              </Text>
            )}
          </Center>
        )}

        {/* Status Badge */}
        {signatureDone !== null && signatureDone !== undefined && (
          <Tooltip
            hasArrow
            label={
              signatureDone
                ? t('documents.messages.documentSigned')
                : canSign
                  ? t('documents.messages.uploadSignedCopy')
                  : t('documents.messages.onlyRecipientMayUploadCopy')
            }
            placement="top"
          >
            <Tag
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                if (onTagClick) {
                  onTagClick()
                }
              }}
              pos="absolute"
              top="8px"
              right="8px"
              zIndex="10"
              size="sm"
              fontSize="11px"
              fontWeight="600"
              borderRadius="6px"
              color={statusColors[statusColor].color}
              bg={statusColors[statusColor].bg}
              borderColor={statusColors[statusColor].border}
              borderWidth="1px"
              opacity="0.8"
              transition="opacity 0.2s ease"
              _groupHover={{ opacity: '1' }}
              px="8px"
              py="4px"
            >
              <TagLeftIcon>{signatureDone ? <CheckCircleFilledIcon /> : <ClockFillIcon />}</TagLeftIcon>
              <TagLabel>
                {signatureDone ? t('documents.status.signed') : t('documents.status.waiting')}
              </TagLabel>
            </Tag>
          </Tooltip>
        )}
      </Box>

      {/* Card Info Section */}
      <Flex
        direction="column"
        borderTopRadius="8px"
        bg="white"
        w="100%"
        p="12px"
        gap="8px"
        boxShadow="0 4px 12px 0 rgba(0,0,0,0.08), 0 2px 6px -1px rgba(0,0,0,0.04)"
      >
        {/* Document Title */}
        <Text fontSize="14px" fontWeight="600" color="solid.900" noOfLines={2} lineHeight="1.3">
          {title}
        </Text>

        {/* Author */}
        <Flex alignItems="center" gap="6px">
          <Avatar size="2xs" w="16px" h="16px" src={author.avatar} name={author.name} />
          <Text fontSize="12px" color="solid.500" noOfLines={1}>
            {author.name}
          </Text>
        </Flex>

        {/* Date */}
        <Flex alignItems="center" gap="6px">
          <Icon w="12px" h="12px" viewBox="0 0 24 24" color="solid.400">
            <CalendarIcon />
          </Icon>
          <Text fontSize="12px" color="solid.500" noOfLines={1}>
            {formatDate(timestamp)}
          </Text>
        </Flex>
      </Flex>
    </MotionBox>
  )
}

export default DocumentCard
